"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { setShallowSearchParams } from "@/lib/hooks/use-shallow-search-param";
import { fragmentSectionAnchor, parseConditionsFragment } from "@/lib/seo/conditions-links";

type EntryChangeTarget = Readonly<{
  addEventListener(type: "currententrychange", listener: () => void): void;
  removeEventListener(type: "currententrychange", listener: () => void): void;
}>;

export type FragmentLensWindow = Readonly<{
  addEventListener(type: "hashchange", listener: () => void): void;
  removeEventListener(type: "hashchange", listener: () => void): void;
  setTimeout(handler: () => void, timeout?: number): number;
  clearTimeout(id: number | undefined): void;
  /** The Navigation API, where the browser has it. */
  navigation?: EntryChangeTarget;
}>;

/**
 * Calls `apply` whenever the URL could have gained a lens fragment without a
 * route change. Next's <Link> navigates with history.pushState, which fires
 * no hashchange, so a same-page link (/conditions/x to /conditions/x#sport=foil)
 * is only visible through the Navigation API's currententrychange. That runs
 * a tick later so Next finishes its own history update first. hashchange
 * still covers typed fragments and browsers without the Navigation API.
 */
export function watchConditionsFragment(win: FragmentLensWindow, apply: () => void): () => void {
  let pending: number | undefined;
  const onEntryChange = () => {
    win.clearTimeout(pending);
    pending = win.setTimeout(apply, 0);
  };
  win.addEventListener("hashchange", apply);
  win.navigation?.addEventListener("currententrychange", onEntryChange);
  return () => {
    win.clearTimeout(pending);
    win.removeEventListener("hashchange", apply);
    win.navigation?.removeEventListener("currententrychange", onEntryChange);
  };
}

/**
 * Applies a #sport= (and #planner=) fragment as the shallow search params the
 * conditions components already read, then scrolls to any section id that
 * follows the lens. Internal links carry the lens in the
 * fragment so crawlers only ever see the canonical spot URL.
 *
 * Runs on first load, on every client-side navigation between conditions
 * routes (the layout stays mounted, so the pathname is the trigger), on
 * same-page history entries and on hash changes. Applying is idempotent: it
 * strips the lens from the hash, so a repeat call finds nothing to do.
 */
export function ConditionsFragmentLens() {
  const pathname = usePathname();
  useEffect(() => {
    const apply = () => {
      const fragment = parseConditionsFragment(window.location.hash);
      if (!fragment) return;
      const url = new URL(window.location.href);
      url.hash = fragment.rest;
      window.history.replaceState(window.history.state, "", url);
      setShallowSearchParams({ ...fragment.params }, { history: "replace" });
      // A section id after the lens (for example #sport=wind&season) is the
      // anchor the link meant; the browser could not scroll to it itself.
      const anchor = fragmentSectionAnchor(fragment.rest);
      if (anchor) document.getElementById(anchor)?.scrollIntoView({ block: "start" });
    };
    apply();
    return watchConditionsFragment(window as unknown as FragmentLensWindow, apply);
  }, [pathname]);
  return null;
}
